export interface ConstellationPattern {
  stars: { x: number; y: number; brightness: number }[];
  lines: [number, number][]; // pairs of star indices to connect
}


// Constellation star patterns for each zodiac sign (1-12)
// Coordinates are normalized to a 0-100 unit box
// Brightness: 0.0 (dim) to 1.0 (bright)
export const CONSTELLATION_PATTERNS: Record<number, ConstellationPattern> = {
  // Aries: curved horn shape
  1: {
    stars: [
      { x: 20, y: 70, brightness: 0.9 },  // Hamal (alpha)
      { x: 35, y: 55, brightness: 0.7 },  // Sheratan (beta)
      { x: 50, y: 45, brightness: 0.5 },  // Mesarthim (gamma)
      { x: 65, y: 30, brightness: 0.4 },  // tip
    ],
    lines: [[0, 1], [1, 2], [2, 3]],
  },

  // Taurus: V-shaped bull head with horns
  2: {
    stars: [
      { x: 50, y: 75, brightness: 1.0 },  // Aldebaran (alpha)
      { x: 35, y: 55, brightness: 0.6 },  // left face
      { x: 65, y: 55, brightness: 0.6 },  // right face
      { x: 20, y: 25, brightness: 0.7 },  // left horn tip (Elnath)
      { x: 80, y: 20, brightness: 0.5 },  // right horn tip (Zeta Tau)
      { x: 42, y: 40, brightness: 0.4 },  // left mid
      { x: 58, y: 40, brightness: 0.4 },  // right mid
    ],
    lines: [[0, 1], [0, 2], [1, 5], [5, 3], [2, 6], [6, 4]],
  },

  // Gemini: two parallel figures (twins)
  3: {
    stars: [
      { x: 30, y: 15, brightness: 1.0 },  // Castor (alpha)
      { x: 60, y: 15, brightness: 0.95 }, // Pollux (beta)
      { x: 28, y: 35, brightness: 0.5 },  // left shoulder
      { x: 62, y: 35, brightness: 0.5 },  // right shoulder
      { x: 25, y: 55, brightness: 0.4 },  // left waist
      { x: 65, y: 55, brightness: 0.4 },  // right waist
      { x: 20, y: 80, brightness: 0.5 },  // left foot
      { x: 70, y: 80, brightness: 0.5 },  // right foot
    ],
    lines: [[0, 2], [2, 4], [4, 6], [1, 3], [3, 5], [5, 7], [2, 3]],
  },

  // Cancer: inverted Y shape (crab claws)
  4: {
    stars: [
      { x: 50, y: 50, brightness: 0.6 },  // Acubens (center)
      { x: 35, y: 30, brightness: 0.5 },  // upper left claw
      { x: 65, y: 30, brightness: 0.5 },  // upper right claw
      { x: 40, y: 70, brightness: 0.7 },  // Asellus Borealis
      { x: 60, y: 70, brightness: 0.7 },  // Asellus Australis
      { x: 50, y: 85, brightness: 0.4 },  // tail
    ],
    lines: [[1, 0], [2, 0], [0, 3], [0, 4], [3, 5], [4, 5]],
  },

  // Leo: sickle + triangle (lion body)
  5: {
    stars: [
      { x: 25, y: 30, brightness: 1.0 },  // Regulus (alpha)
      { x: 30, y: 15, brightness: 0.6 },  // Eta Leo (head)
      { x: 45, y: 10, brightness: 0.5 },  // Mu Leo
      { x: 55, y: 18, brightness: 0.5 },  // Zeta Leo
      { x: 45, y: 35, brightness: 0.6 },  // Algieba (gamma)
      { x: 65, y: 45, brightness: 0.5 },  // Delta Leo
      { x: 80, y: 55, brightness: 0.8 },  // Denebola (beta)
      { x: 60, y: 65, brightness: 0.4 },  // Theta Leo
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 0], [4, 5], [5, 6], [6, 7], [7, 5]],
  },

  // Virgo: Y with extended arms
  6: {
    stars: [
      { x: 50, y: 80, brightness: 1.0 },  // Spica (alpha)
      { x: 45, y: 60, brightness: 0.5 },  // junction
      { x: 35, y: 40, brightness: 0.5 },  // left arm
      { x: 55, y: 40, brightness: 0.5 },  // right arm
      { x: 25, y: 20, brightness: 0.6 },  // left hand (Vindemiatrix)
      { x: 65, y: 20, brightness: 0.4 },  // right hand
      { x: 45, y: 25, brightness: 0.4 },  // head
    ],
    lines: [[0, 1], [1, 2], [1, 3], [2, 4], [3, 5], [2, 6], [3, 6]],
  },

  // Libra: scales / balance beam
  7: {
    stars: [
      { x: 50, y: 50, brightness: 0.6 },  // fulcrum
      { x: 25, y: 35, brightness: 0.8 },  // Zubenelgenubi (alpha)
      { x: 75, y: 35, brightness: 0.7 },  // Zubeneschamali (beta)
      { x: 20, y: 55, brightness: 0.4 },  // left pan bottom
      { x: 80, y: 55, brightness: 0.4 },  // right pan bottom
      { x: 50, y: 75, brightness: 0.5 },  // base
    ],
    lines: [[1, 0], [0, 2], [1, 3], [2, 4], [0, 5]],
  },

  // Scorpio: curved hook tail
  8: {
    stars: [
      { x: 20, y: 35, brightness: 0.6 },  // head left
      { x: 35, y: 30, brightness: 0.6 },  // head right
      { x: 30, y: 45, brightness: 1.0 },  // Antares (alpha)
      { x: 35, y: 60, brightness: 0.5 },  // body
      { x: 45, y: 70, brightness: 0.5 },  // body 2
      { x: 58, y: 75, brightness: 0.5 },  // tail curve
      { x: 72, y: 72, brightness: 0.6 },  // Shaula (lambda)
      { x: 80, y: 65, brightness: 0.5 },  // stinger
    ],
    lines: [[0, 2], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7]],
  },

  // Sagittarius: arrow / bow shape
  9: {
    stars: [
      { x: 30, y: 70, brightness: 0.6 },  // bow bottom
      { x: 25, y: 45, brightness: 0.5 },  // bow middle
      { x: 35, y: 25, brightness: 0.6 },  // bow top
      { x: 45, y: 45, brightness: 0.7 },  // arrow notch
      { x: 70, y: 20, brightness: 0.8 },  // arrowhead (Kaus Media)
      { x: 55, y: 65, brightness: 0.5 },  // body
      { x: 65, y: 80, brightness: 0.4 },  // base
    ],
    lines: [[0, 1], [1, 2], [1, 3], [3, 4], [3, 5], [5, 6]],
  },

  // Capricorn: sea-goat triangle
  10: {
    stars: [
      { x: 25, y: 30, brightness: 0.7 },  // Algedi (alpha)
      { x: 45, y: 25, brightness: 0.6 },  // Dabih (beta)
      { x: 70, y: 35, brightness: 0.5 },  // Nashira
      { x: 80, y: 55, brightness: 0.6 },  // Deneb Algedi (delta)
      { x: 60, y: 65, brightness: 0.4 },  // body
      { x: 35, y: 60, brightness: 0.4 },  // tail
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0]],
  },

  // Aquarius: zigzag water stream
  11: {
    stars: [
      { x: 25, y: 25, brightness: 0.6 },  // Sadalmelik (alpha)
      { x: 45, y: 20, brightness: 0.7 },  // Sadalsuud (beta)
      { x: 35, y: 40, brightness: 0.5 },  // wave 1
      { x: 55, y: 40, brightness: 0.5 },  // wave 2
      { x: 45, y: 60, brightness: 0.4 },  // wave 3
      { x: 65, y: 60, brightness: 0.4 },  // wave 4
      { x: 55, y: 80, brightness: 0.5 },  // stream end
    ],
    lines: [[0, 1], [0, 2], [1, 3], [2, 3], [3, 4], [4, 5], [5, 6]],
  },

  // Pisces: two fish connected by cord
  12: {
    stars: [
      { x: 25, y: 25, brightness: 0.6 },  // fish 1 head
      { x: 35, y: 35, brightness: 0.4 },  // fish 1 tail
      { x: 45, y: 50, brightness: 0.5 },  // cord knot (Al Rischa)
      { x: 55, y: 65, brightness: 0.4 },  // fish 2 tail
      { x: 75, y: 75, brightness: 0.6 },  // fish 2 head
      { x: 20, y: 15, brightness: 0.4 },  // fish 1 fin
      { x: 80, y: 85, brightness: 0.4 },  // fish 2 fin
    ],
    lines: [[5, 0], [0, 1], [1, 2], [2, 3], [3, 4], [4, 6]],
  },
};
